"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { forwardRef, type ComponentPropsWithoutRef } from "react";

import { cx } from "./cx";
import styles from "./radio.module.css";

/**
 * Container for a set of mutually-exclusive options. Renders a div with
 * `role="radiogroup"`. Pass `aria-labelledby` (preferred) or `aria-label`
 * to give the group an accessible name. Forwards `className` so consumers
 * can apply their own layout (grid, flex, etc.) on top.
 */
export const RadioGroup = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cx(styles.group, className)}
    {...props}
  />
));
RadioGroup.displayName = "RadioGroup";

/**
 * Basic 16px radio dot. Use inside a `<label>` for label + description
 * layout. Parallel API to `Checkbox` from this same package.
 */
export const Radio = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cx(styles.radio, className)}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className={styles.indicator} />
  </RadioGroupPrimitive.Item>
));
Radio.displayName = "Radio";

interface RadioCardProps extends Omit<
  ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
  "children"
> {
  label: string;
  description?: string;
  /**
   * Optional hue (any CSS color) applied as `--section-hue` on the tile.
   * Drives the selected-state ribbon, border tint, corner glow, and
   * background tint. Falls back to `--color-primary` when unset. Mirrors
   * the `data-section` pattern from /settings/templates.
   */
  hue?: string;
}

/**
 * Rich card-style radio option — title + optional description rendered
 * inside a clickable, full-row card. Used by the brief wizard's option
 * pickers (tone, audience, goal, surface, brand context). Pairs with
 * `RadioGroup` for proper radiogroup semantics + arrow-key navigation +
 * focus-visible styling.
 */
export const RadioCard = forwardRef<HTMLButtonElement, RadioCardProps>(
  ({ label, description, className, hue, style, ...props }, ref) => (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cx(styles.card, className)}
      style={hue ? { ...style, ["--section-hue" as string]: hue } : style}
      {...props}
    >
      <div className={styles.cardBody}>
        <div className={styles.cardLabel}>{label}</div>
        {description && <div className={styles.cardDesc}>{description}</div>}
      </div>
      <span aria-hidden className={styles.cardTick}>
        <RadioGroupPrimitive.Indicator className={styles.cardTickInner} />
      </span>
    </RadioGroupPrimitive.Item>
  ),
);
RadioCard.displayName = "RadioCard";
